// One product's stock, lot by lot (plan §5.3): every non-zero balance row with its lot,
// best-before, expiry state, location, quantity and the movement that last touched it.
// Stock from a sales replay is never here (it lives in the replay's hidden branch, D-036).

import type { StateTask, StockLotRow } from '@enp/domain/api';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  cn,
  createColumnHelper,
  DataTable,
  EmptyState,
  PageHeader,
  Qty,
  Skeleton,
  type ColumnDef,
} from '@enp/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  notFound,
  useNavigate,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ClipboardList,
  FlaskConical,
  History,
  PackageOpen,
  PackageSearch,
  PauseCircle,
  PackageX,
  TriangleAlert,
} from 'lucide-react';
import { meQueryOptions } from '../../../auth/session.js';
import { ApiError } from '../../../lib/api.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { useDocumentTitle } from '../../../shell/page.js';
import {
  BestBefore,
  LEDGER_DECIMALS,
  StateQtyBadges,
  StockStateBadge,
} from '../../../stock/components.js';
import {
  LOCATION_TYPE_LABEL,
  LOT_SOURCE_LABEL,
  PROVENANCE_LABEL,
  REASON_LABEL,
  calendarToday,
} from '../../../stock/labels.js';
import { levelsByStateFromRows } from '../../../stock/levels.js';
import { lotsQuery } from '../../../stock/queries.js';
import { stateTasksQuery } from '../../../stock-control/queries.js';
import { stockStateLabel } from '../../../stock/labels.js';
import { AccountDate } from '../../../shell/account-date.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/stock/$productId')({
  loader: async ({ context, params }) => {
    if (!UUID_RE.test(params.productId)) throw notFound();
    try {
      await context.queryClient.ensureQueryData(lotsQuery(params.productId));
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: LotsPage,
  pendingComponent: LotsSkeleton,
  errorComponent: LotsError,
  notFoundComponent: LotsNotFound,
});

const TABLE_LABEL = 'Lots';

function BackToStock({ name }: { name: string }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/stock">Stock</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{name}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/** Today's expiry-list move for a lot, linked to the list (lots → their state tasks). */
function TodaysMove({ task }: { task: StateTask }) {
  const done = task.status !== 'pending';
  return (
    <Link
      to="/stock/expiry"
      search={{ lot: task.lot.id }}
      // The row opens the lot's movements; this opens the expiry list.
      onClick={(e) => e.stopPropagation()}
      className="inline-flex w-fit items-center gap-1 rounded-sm text-xs font-medium text-primary-text underline-offset-2 hover:underline"
    >
      {done ? 'Today’s move: ' : 'Moves today: '}
      {stockStateLabel(task.from_state)}
      <ArrowRight aria-label="to" className="size-3" />
      {stockStateLabel(task.to_state)}
      {done
        ? ` (${task.status === 'done' ? 'done' : task.status === 'skipped' ? 'skipped' : 'carried to next day'})`
        : ''}
    </Link>
  );
}

function lotColumns(
  today: string,
  unit: string,
  moves: Map<string, StateTask>,
): ColumnDef<StockLotRow, unknown>[] {
  const col = createColumnHelper<StockLotRow>();
  return [
    col.display({
      id: 'lot',
      header: 'Lot',
      meta: { width: 'min-w-44' },
      cell: ({ row }) => {
        const lot = row.original.lot;
        if (!lot) return <span className="text-muted">Not tracked by lot</span>;
        return (
          <span className="flex flex-col gap-1 leading-tight">
            <span className="whitespace-nowrap font-mono text-sm font-medium text-fg">
              {lot.lot_code}
            </span>
            <span className="flex flex-wrap gap-1">
              {lot.synthetic && (
                // Should never happen (D-036): practice stock is kept in its own branch.
                <Badge tone="danger">
                  <FlaskConical aria-hidden />
                  Practice lot: not real stock
                </Badge>
              )}
              {lot.status === 'on_hold' && (
                <Badge tone="warning">
                  <PauseCircle aria-hidden />
                  On hold
                </Badge>
              )}
              {lot.status === 'closed' && <Badge tone="outline">Closed</Badge>}
            </span>
            {moves.get(`${lot.id}|${row.original.location.id}|${row.original.state}`) && (
              <TodaysMove
                task={
                  moves.get(
                    `${lot.id}|${row.original.location.id}|${row.original.state}`,
                  ) as StateTask
                }
              />
            )}
          </span>
        );
      },
    }),
    col.display({
      id: 'best_before',
      header: 'Best-before',
      cell: ({ row }) => <BestBefore date={row.original.lot?.best_before ?? null} today={today} />,
    }),
    col.display({
      id: 'state',
      header: 'State',
      cell: ({ row }) => <StockStateBadge state={row.original.state} />,
    }),
    col.display({
      id: 'location',
      header: 'Location',
      cell: ({ row }) => (
        <span className="flex flex-col whitespace-nowrap leading-tight">
          <span className="text-fg">{row.original.location.name}</span>
          <span className="text-xs text-muted">
            {LOCATION_TYPE_LABEL[row.original.location.type]}
          </span>
        </span>
      ),
    }),
    col.display({
      id: 'qty',
      header: 'Quantity',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Qty
          value={row.original.qty}
          unit={unit}
          maxDecimals={LEDGER_DECIMALS}
          className={cn(
            'font-semibold',
            row.original.qty.startsWith('-') ? 'text-state-danger' : 'text-fg',
          )}
        />
      ),
    }),
    col.display({
      id: 'last',
      header: 'Last movement',
      meta: { className: 'hidden lg:table-cell' },
      cell: ({ row }) => {
        const m = row.original.last_movement;
        if (!m) return <span className="text-muted">None recorded</span>;
        return (
          <span className="flex flex-col leading-tight">
            <span className="text-fg">{REASON_LABEL[m.reason]}</span>
            <span className="text-xs text-muted">
              {PROVENANCE_LABEL[m.provenance]} · <AccountDate value={m.occurred_at} withTime />
            </span>
          </span>
        );
      },
    }),
    col.display({
      id: 'source',
      header: 'Came from',
      meta: { className: 'hidden xl:table-cell' },
      cell: ({ row }) => {
        const lot = row.original.lot;
        if (!lot) return <span className="text-muted">—</span>;
        return (
          <span className="flex flex-col leading-tight">
            <span className="text-fg">{LOT_SOURCE_LABEL[lot.source]}</span>
            {lot.supplier_lot_ref && (
              <span className="text-xs text-muted">Supplier lot {lot.supplier_lot_ref}</span>
            )}
          </span>
        );
      },
    }),
  ];
}

function LotsPage() {
  const { productId } = Route.useParams();
  const { data } = useSuspenseQuery(lotsQuery(productId));
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const navigate = useNavigate();
  useDocumentTitle(`${data.product.name} · Stock`);
  // Best-before judgements (days left, near expiry) use the calendar date, not the trading day.
  const today = calendarToday(me.account.timezone);
  const unit = data.product.base_uom;
  const { total, states } = levelsByStateFromRows(data.rows);
  const lotCount = new Set(data.rows.flatMap((r) => (r.lot ? [r.lot.id] : []))).size;
  const practice = data.rows.filter((r) => r.lot?.synthetic).length;
  // Today's expiry list, for the lots' moves (quietly nothing when it can't be read).
  const tasks = useQuery(stateTasksQuery(undefined));
  const moves = new Map(
    (tasks.data?.items ?? [])
      .filter((t) => t.product.id === productId)
      // A done move's stock is in its new state now.
      .map(
        (t) =>
          [
            `${t.lot.id}|${t.location.id}|${t.status === 'done' ? t.to_state : t.from_state}`,
            t,
          ] as const,
      ),
  );

  return (
    <>
      <PageHeader
        eyebrow={<BackToStock name={data.product.name} />}
        title={data.product.name}
        description={
          data.rows.length === 0 ? (
            'Nothing on hand.'
          ) : (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span className="text-fg">
                <Qty value={total} unit={unit} className="font-semibold" /> on hand
                {lotCount > 0 && ` in ${lotCount} ${lotCount === 1 ? 'lot' : 'lots'}`}
              </span>
              <StateQtyBadges states={states} unit={unit} />
            </span>
          )
        }
        actions={
          <>
            <Button variant="secondary" asChild>
              <Link to="/stock/movements" search={{ product: productId }}>
                <History aria-hidden />
                Movements
              </Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to="/stock/counts" search={{ product: productId }}>
                <ClipboardList aria-hidden />
                Counts
              </Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to="/stock/write-offs" search={{ product: productId }}>
                <PackageX aria-hidden />
                Write-offs
              </Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to="/catalogue/$productId" params={{ productId }}>
                <BookOpen aria-hidden />
                Catalogue entry
              </Link>
            </Button>
          </>
        }
      />
      {practice > 0 && (
        // A sales replay's invented stock must never reach real stock (D-036, review
        // UI2A-9): if it ever does, it is an error to report, not a friendly label.
        <div
          role="alert"
          className="mb-4 flex items-start gap-3 rounded-md border border-state-danger/40 bg-state-danger-subtle px-4 py-3 text-body text-fg"
        >
          <TriangleAlert aria-hidden className="mt-0.5 size-5 shrink-0 text-state-danger" />
          <p>
            <strong className="font-semibold">
              {practice === 1 ? 'A practice lot is' : `${practice} practice lots are`} showing as
              real stock.
            </strong>{' '}
            Stock invented by a sales replay should stay in its practice branch. Don’t sell or count
            it; tell support so it can be put right.
          </p>
        </div>
      )}
      <DataTable
        label={TABLE_LABEL}
        columns={lotColumns(today, unit, moves)}
        data={data.rows}
        filterable={false}
        getRowId={(r, i) => `${r.lot?.id ?? 'none'}:${r.location.id}:${r.state}:${i}`}
        onRowActivate={(r) =>
          void navigate({
            to: '/stock/movements',
            search: r.lot ? { product: productId, lot: r.lot.id } : { product: productId },
          })
        }
        empty={
          <EmptyState
            icon={PackageOpen}
            title={`No ${data.product.name} on hand`}
            description="Every lot has been sold, used or written off. The movements show where it went."
            action={
              <Button asChild>
                <Link to="/stock/movements" search={{ product: productId }}>
                  <History aria-hidden />
                  See its movements
                </Link>
              </Button>
            }
          />
        }
        footer={
          data.rows.length > 0 ? (
            <p className="text-sm text-muted">
              Soonest best-before first. Open a lot to see every movement it had.
            </p>
          ) : undefined
        }
      />
    </>
  );
}

function LotsSkeleton() {
  return (
    <div role="status" aria-live="polite">
      <h1 className="sr-only">Loading stock</h1>
      <span className="sr-only">Loading stock…</span>
      <div className="flex flex-col gap-2 pb-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-6 w-96 max-w-full" />
      </div>
      <Skeleton className="h-80 w-full rounded-lg" />
    </div>
  );
}

function BackButton() {
  return (
    <Button variant="secondary" asChild>
      <Link to="/stock">
        <ArrowLeft aria-hidden />
        Back to stock
      </Link>
    </Button>
  );
}

function LotsError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <div className="max-w-2xl">
      <ErrorPanel
        error={error}
        what="This product’s stock"
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
        extraAction={<BackButton />}
      />
    </div>
  );
}

function LotsNotFound() {
  useDocumentTitle('Product not found');
  // Full width, with the way back above it, like the page it stands in for (UI2A-19).
  return (
    <>
      <div className="pb-4">
        <BackToStock name="Not found" />
      </div>
      <EmptyState
        icon={PackageSearch}
        titleAs="h1"
        title="Product not found"
        description="There’s no product at this address. It may be in another account, or the link may be mistyped."
        action={<BackButton />}
      />
    </>
  );
}
